import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Medal, Swords, Trophy } from 'lucide-react';

import { env } from '@/env';
import { useActiveMatchContext } from '@/contexts/ActiveMatchContext';
import { Button } from '@/components/common/Button';
import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import { useHaptics } from '@/hooks/useHaptics';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { getMyLogtoSub } from '@/lib/logto';
import {
  corsaAperta,
  corseInAttesa,
  dimenticaCorsa,
  dimenticaIncontro,
  eventoCorsa,
  eventoIncontro,
  incontroDecisoOf,
  mostrabile,
  osserva,
  postoFinaleOf,
  quandoEsitoOf,
  ricordaIncontro,
  schermataDiGioco,
  segnaVista,
  torneoTerminale,
  type EventoEsito,
  type MemoriaEsiti,
  type Sparito,
} from '@/lib/limulo/esiti';
import { caricaMemoria, salvaMemoria, vistaNelloStorage } from '@/lib/limulo/memoriaEsiti';
import { nomeCodaDi } from '@/lib/limulo/partiteInCorso';
import { limuloMatchUrl, limuloTournamentUrl } from '@/lib/limulo/routes';
import { useTornaAllaCoda } from '@/hooks/useTornaAllaCoda';
import { cn } from '@/lib/cn';
import { ModaleEsitoV3, useV3 } from '@/lib/v3';
import { limuloApi, useGetLimuloAzioniQuery, useGetLimuloTournamentQuery } from '@/services/limuloApi';
import { storicoEsitoOf } from '@/lib/limulo/model';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { LimuloTournament } from '@/types/limulo';

/**
 * 🔴 LA MODALE DELL'ESITO — Luca, 2026-09-29T11:51:52Z, testuale: «Una volta fatto risultato vinto/perso
 * match piazzamento/vittoria torneo (o sit) bisogna che venga mostrata una modale con l'informazione.»
 * Quando un esito nasce e quando si puo' dire lo decide `lib/limulo/esiti.ts`; qui ci sono la memoria,
 * le letture e il disegno (quello di `ModaleCheckIn`, niente sistema di modali nuovo).
 *
 * **Costo di rete** (seggio, decisione 7): nessun poll nuovo.
 *  · le azioni sono la STESSA voce di cache della barra (`useGetLimuloAzioniQuery(undefined)`), letta
 *    senza `pollingInterval`: il ritmo resta quello della barra, e in regime qui non parte nessuna GET;
 *  · per un incontro sparito: 1 GET della scheda d'incontro; se era il mio ultimo incontro aperto nel
 *    torneo, 1 GET della scheda torneo e, se il torneo e' concluso, 1 GET della classifica;
 *  · una corsa che aspetta la fine del torneo si abbona alla scheda torneo SENZA polling: la rilegge il
 *    tempo reale quando il torneo cambia stato (`tagDaRileggereOf`, `tournament-state`), il ritorno in
 *    primo piano e l'apertura dell'app. Il conto e' in `tests/modale-esito-client-vero.test.tsx`.
 *
 * **Quando si mostra** (seggio, condizioni dell'ok 12:03:05Z): una alla volta, in fila; mai sopra
 * un'altra modale (qualunque elemento `aria-modal`: il check-in, i fogli, il tour), e mai durante una
 * partita dal vivo (`descriptor` della partita attiva, o le rotte di gioco di `schermataDiGioco`). Se una
 * di queste compare mentre l'esito e' aperto, l'esito si nasconde e torna quando la via e' libera.
 * ⚠️ «Un'altra modale» = un elemento con `aria-modal`: RegionPickerModal, GamePicker, ReskinModal,
 * DraftResumePrompt e WatchPartyHub (z-50, senza `aria-modal`) NON li vede (revisione di #503, 🟡5).
 */
export function OsservatoreEsiti() {
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);
  // Stessi cancelli della barra delle azioni del motore (`AzionePersistente`): flag di build, ruolo beta,
  // sessione, e la lettura unica delle azioni (kb#275) — senza quella non c'e' una sorgente da riusare.
  const isBeta = useHasLogtoRole('beta');
  if (!env.featureLimuloEnabled || !env.featureTournamentActionsEnabled || !isBeta || !authed) return null;
  return <Osservatore />;
}

/** Quante volte si ritenta la scheda di un incontro sparito che non risponde, prima di lasciarlo. */
const TENTATIVI_INCONTRO = 3;

function Osservatore() {
  const dispatch = useAppDispatch();
  const lettura = useGetLimuloAzioniQuery(undefined);
  const sub = useMemo(() => getMyLogtoSub() ?? '', []);
  const memoria = useRef<MemoriaEsiti | null>(null);
  if (memoria.current === null) memoria.current = caricaMemoria(sub, Date.now());
  const [coda, setCoda] = useState<EventoEsito[]>([]);
  const [attesa, setAttesa] = useState<string[]>(() => corseInAttesa(memoria.current as MemoriaEsiti, Date.now()).attesa);
  const inLettura = useRef(new Set<string>());
  const tentativi = useRef(new Map<string, number>());

  const aggiorna = useCallback(
    (fn: (m: MemoriaEsiti) => MemoriaEsiti) => {
      memoria.current = salvaMemoria(sub, fn(memoria.current as MemoriaEsiti));
    },
    [sub],
  );
  const ricalcolaAttesa = useCallback(() => {
    const { attesa: a, scadute } = corseInAttesa(memoria.current as MemoriaEsiti, Date.now());
    if (scadute.length > 0) aggiorna((m) => scadute.reduce(dimenticaCorsa, m));
    setAttesa((prima) => (prima.length === a.length && prima.every((x, i) => x === a[i]) ? prima : a));
  }, [aggiorna]);
  const accoda = useCallback((ev: EventoEsito, quando: number | null) => {
    if (!mostrabile(memoria.current as MemoriaEsiti, ev.chiave, quando, Date.now())) return;
    const conOra = { ...ev, quando };
    setCoda((c) => (c.some((x) => x.chiave === ev.chiave) ? c : [...c, conOra]));
  }, []);
  const segna = useCallback(
    (ev: EventoEsito) => aggiorna((m) => segnaVista(m, ev.chiave, ev.quando ?? Date.now(), Date.now())),
    [aggiorna],
  );
  const vistaAltrove = useCallback(
    (chiave: string) => {
      if (!vistaNelloStorage(sub, chiave)) return false;
      // Fondere le viste dello storage nella nostra memoria: il prossimo salvataggio le tiene.
      aggiorna((m) => m);
      return true;
    },
    [sub, aggiorna],
  );

  /** La fine della corsa, a torneo terminale: 1 GET della classifica, poi la corsa si dimentica. */
  const chiudiCorsa = useCallback(
    async (tournamentId: string, torneo: LimuloTournament, matchId: string | null): Promise<EventoEsito | null> => {
      const corsa = (memoria.current as MemoriaEsiti).corse[tournamentId];
      if (!corsa) return null;
      let posto: number | null = null;
      // Solo un torneo CONCLUSO ha una classifica da leggere: su uno annullato la GET sarebbe sprecata.
      if (storicoEsitoOf(torneo) === 'concluso') {
        const classifica = await dispatch(
          limuloApi.endpoints.getLimuloStandings.initiate(tournamentId, { subscribe: false, forceRefetch: true }),
        )
          .unwrap()
          .catch(() => null);
        posto = postoFinaleOf(classifica, corsa.partyId);
      }
      const ev = eventoCorsa({ tournamentId, torneo, corsa, posto, matchId });
      aggiorna((m) => dimenticaCorsa(m, tournamentId));
      return ev;
    },
    [dispatch, aggiorna],
  );

  const risolvi = useCallback(
    async (s: Sparito) => {
      const torneoNome = (memoria.current as MemoriaEsiti).osservati[s.matchId]?.torneo ?? null;
      const match = await dispatch(
        limuloApi.endpoints.getLimuloMatch.initiate(
          { id: s.tournamentId, matchId: s.matchId },
          { subscribe: false, forceRefetch: true },
        ),
      )
        .unwrap()
        .catch(() => undefined);
      if (match === undefined) {
        // Non ha risposto. ⚠️ Si ritenta solo alla prossima lettura delle azioni che CAMBIA la lista: con la
        // lista invariata RTK tiene lo stesso `data` (condivisione strutturale) e l'effetto non riparte.
        // Dopo `TENTATIVI_INCONTRO` fallimenti l'incontro si lascia, in silenzio: quell'esito non si dira'.
        // (Revisione di #503, 🟡4. Gemello: se fallisce la CLASSIFICA, a fine corsa esce l'esito
        // dell'incontro invece del piazzamento — vero, ma meno.)
        const n = (tentativi.current.get(s.matchId) ?? 0) + 1;
        tentativi.current.set(s.matchId, n);
        if (n >= TENTATIVI_INCONTRO) aggiorna((m) => dimenticaIncontro(m, s.matchId));
        return;
      }
      aggiorna((m) => dimenticaIncontro(m, s.matchId));
      const deciso = incontroDecisoOf(match);
      if (deciso === null) return;
      aggiorna((m) => ricordaIncontro(m, s.tournamentId, deciso));
      // La corsa e' aperta se questo non e' il mio ultimo incontro li': la modale dell'incontro aspetta e NON rilegge il torneo
      // (una GET per evento, non di piu': `tests/modale-esito-client-vero.test.tsx`, «il costo di rete»). Per sapere se la
      // partita e' di coda o di sit&go (`presentazioneDi`) basta la voce di cache che la sonda della corsa (`SondaCorsa`) tiene
      // gia' calda; senza, decide il solo prefisso dell'id.
      const aperta = corsaAperta(memoria.current as MemoriaEsiti, s.tournamentId);
      // Il torneo si legge PRIMA dell'esito: e' da lui che si sa se la partita e' di coda o di sit&go.
      const torneo = aperta
        ? dispatch((_dispatch, getState) =>
            (limuloApi.endpoints.getLimuloTournament.select(s.tournamentId)(getState()).data ?? null) as LimuloTournament | null,
          )
        : await dispatch(
            limuloApi.endpoints.getLimuloTournament.initiate(s.tournamentId, { subscribe: false, forceRefetch: true }),
          )
            .unwrap()
            .catch(() => null);
      const ev = eventoIncontro(deciso, s, torneoNome, torneo);
      if (aperta) {
        accoda(ev, deciso.quando);
        return;
      }
      // Era il mio ultimo incontro aperto li': il torneo e' finito? Se si', UNA modale sola, quella della
      // corsa (seggio, decisione 2); se no, quella dell'incontro, e la corsa aspetta.
      if (torneo && torneoTerminale(torneo)) {
        const corsa = await chiudiCorsa(s.tournamentId, torneo, s.matchId);
        if (corsa !== null) accoda(corsa, deciso.quando);
        else accoda(ev, deciso.quando);
        return;
      }
      // 🔴 [revisione di #503, 🟡2] Il motore suona `reason: 'result'` PRIMA di chiudere il torneo, quindi
      // qui il torneo puo' essere ancora aperto anche quando la corsa e' finita. Nella coda 1v1 il torneo E'
      // l'incontro: l'esito dell'incontro chiude anche la corsa, o a fine torneo uscirebbe un secondo «Hai
      // perso» identico. Senza scheda si decide dal prefisso dell'id (`presentazioneDi`, la regola della Live).
      if (ev.presentazione === 'coda') {
        aggiorna((m) => segnaVista(dimenticaCorsa(m, s.tournamentId), `t:${s.tournamentId}`, deciso.quando, Date.now()));
      }
      accoda(ev, deciso.quando);
      ricalcolaAttesa();
    },
    [dispatch, aggiorna, accoda, chiudiCorsa, ricalcolaAttesa],
  );

  useEffect(() => {
    if (lettura.data === undefined) return;
    const { memoria: m, spariti } = osserva(memoria.current as MemoriaEsiti, lettura.data);
    aggiorna(() => m);
    for (const s of spariti) {
      if (inLettura.current.has(s.matchId)) continue;
      inLettura.current.add(s.matchId);
      void risolvi(s).finally(() => inLettura.current.delete(s.matchId));
    }
    ricalcolaAttesa();
  }, [lettura.data, aggiorna, risolvi, ricalcolaAttesa]);

  const suTerminale = useCallback(
    async (tournamentId: string, torneo: LimuloTournament) => {
      if (inLettura.current.has(`t:${tournamentId}`)) return;
      inLettura.current.add(`t:${tournamentId}`);
      try {
        const corsa = (memoria.current as MemoriaEsiti).corse[tournamentId];
        const ev = await chiudiCorsa(tournamentId, torneo, null);
        if (ev !== null) accoda(ev, corsa?.ultimo ?? null);
        ricalcolaAttesa();
      } finally {
        inLettura.current.delete(`t:${tournamentId}`);
      }
    },
    [chiudiCorsa, accoda, ricalcolaAttesa],
  );

  const prossimaDi = useCallback(
    (ev: EventoEsito): string | null => {
      const voce = lettura.data?.items.find(
        (v) => v.tournamentId === ev.tournamentId && v.matchId !== null && v.matchId !== ev.matchId,
      );
      return voce?.matchId ?? null;
    },
    [lettura.data],
  );

  return (
    <>
      {attesa.map((id) => (
        <SondaCorsa key={id} tournamentId={id} suTerminale={suTerminale} />
      ))}
      <FilaEsiti
        coda={coda}
        togli={() => setCoda((c) => c.slice(1))}
        segna={segna}
        vistaAltrove={vistaAltrove}
        prossimaDi={prossimaDi}
      />
    </>
  );
}

/**
 * La scheda di un torneo in cui la mia corsa aspetta la fine. Abbonata SENZA polling: la rileggono il tempo
 * reale (`tournament-state`), il ritorno in primo piano e un'altra schermata che la legge gia'.
 */
function SondaCorsa({
  tournamentId,
  suTerminale,
}: {
  tournamentId: string;
  suTerminale: (id: string, torneo: LimuloTournament) => void;
}) {
  const { data } = useGetLimuloTournamentQuery(tournamentId, { refetchOnFocus: true });
  useEffect(() => {
    if (data && torneoTerminale(data)) suTerminale(tournamentId, data);
  }, [data, tournamentId, suTerminale]);
  return null;
}

/** C'e' un'altra modale aperta? Qualunque `aria-modal` che non sia la nostra. */
const altraModaleAperta = (): boolean =>
  typeof document !== 'undefined' && document.querySelector('[aria-modal]:not([data-modale-esito])') !== null;

/**
 * 🔴 [revisione di #503, 🟡1] La risposta si legge dal DOM AL RENDER, non da uno stato che parte a `false`:
 * con un'altra modale gia' aperta, il primo render avrebbe aperto l'esito per un istante (aptica e «gia'
 * vista» comprese) prima che l'effetto se ne accorgesse. L'osservatore serve solo a far rileggere quando
 * il DOM cambia.
 */
function useAltraModaleAperta(attivo: boolean): boolean {
  const [, rileggi] = useState(0);
  useEffect(() => {
    if (!attivo) return;
    const leggi = () => rileggi((n) => n + 1);
    const osservatore = new MutationObserver(leggi);
    osservatore.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal'],
    });
    return () => osservatore.disconnect();
  }, [attivo]);
  return attivo && altraModaleAperta();
}

function FilaEsiti({
  coda,
  togli,
  segna,
  vistaAltrove,
  prossimaDi,
}: {
  coda: EventoEsito[];
  togli: () => void;
  segna: (ev: EventoEsito) => void;
  vistaAltrove: (chiave: string) => boolean;
  prossimaDi: (ev: EventoEsito) => string | null;
}) {
  const location = useLocation();
  const { descriptor } = useActiveMatchContext();
  const haptics = useHaptics();
  const [aperto, setAperto] = useState<EventoEsito | null>(null);
  // L'esito che ha passato il controllo dello storage ed e' pronto a salire a schermo.
  const [verificato, setVerificato] = useState<string | null>(null);
  // Gli esiti che QUESTA fila ha gia' messo a schermo: al ritorno dopo essere stati nascosti non vibrano
  // di nuovo e non si risegnano (🟢13).
  const montati = useRef(new Set<string>());
  const altra = useAltraModaleAperta(coda.length > 0 || aperto !== null);
  const bloccata = descriptor != null || schermataDiGioco(location.pathname) || altra;

  useEffect(() => {
    if (aperto !== null || bloccata || coda.length === 0) return;
    const [primo] = coda;
    togli();
    setAperto(primo);
  }, [aperto, bloccata, coda, togli]);

  // 🔴 [revisione di #503, 🟡8] Lo storage si rilegge quando la via e' LIBERA, subito prima di mostrare,
  // non all'uscita dalla fila: fra le due un check-in puo' averlo nascosto, e intanto un'altra scheda puo'
  // averlo mostrato. Si verifica una volta sola per esito: uno gia' verificato (e quindi montato, o a un
  // render dal montaggio) torna senza controllo, perche' «visto» nello storage l'ha scritto questa fila.
  // ⚠️ Finestra residua dichiarata: verificato e poi nascosto nel render prima del montaggio, torna senza
  // rileggere.
  useEffect(() => {
    if (aperto === null || bloccata || verificato === aperto.chiave) return;
    if (vistaAltrove(aperto.chiave)) {
      setAperto(null);
      return;
    }
    setVerificato(aperto.chiave);
  }, [aperto, bloccata, verificato, vistaAltrove]);

  const suMontata = useCallback(
    (ev: EventoEsito) => {
      if (montati.current.has(ev.chiave)) return;
      montati.current.add(ev.chiave);
      segna(ev);
      // 🟢16 (seggio): su «N° posto» un'aptica NEUTRA — il titolo non dice vinto ne' perso.
      if (ev.tipo === 'posto') haptics.impact('light');
      else haptics.notification(ev.tipo === 'perso' ? 'warning' : 'success');
    },
    [segna, haptics],
  );

  // Se qualcosa di piu' urgente compare sopra (il check-in, una partita, una schermata di gioco) l'esito
  // aperto si NASCONDE e resta suo: torna appena la via e' libera. Niente fila da riordinare.
  if (aperto === null || bloccata || verificato !== aperto.chiave) return null;
  return (
    <Modale evento={aperto} prossima={prossimaDi(aperto)} suMontata={suMontata} onChiudi={() => setAperto(null)} />
  );
}

/** Le props della modale: le stesse per quella di oggi e per quella della v3, cosi' `FilaEsiti` non sa quale sia. */
export interface PropsModaleEsito {
  evento: EventoEsito;
  prossima: string | null;
  suMontata: (ev: EventoEsito) => void;
  onChiudi: () => void;
}

/**
 * IL BIVIO v3 / oggi (fase 4, PR 5): UN punto solo. Con la v3 accesa (`useV3()`) la modale e' `ModaleEsitoV3`, caricata a richiesta; altrimenti quella di oggi, `ModaleOggi`, com'e'. ⚠️ Il `fallback` del `Suspense` e' `null`,
 * MAI la modale di oggi: monterebbe e chiamerebbe `suMontata` prima della v3, e «vista» e l'aptica partirebbero sulla pelle sbagliata; l'esito resta in fila un attimo. Il «quando» e' di `FilaEsiti`, non di questo bivio.
 */
export function Modale(props: PropsModaleEsito) {
  const v3 = useV3();
  if (v3 && ModaleEsitoV3) {
    return (
      <Suspense fallback={null}>
        <ModaleEsitoV3 {...props} />
      </Suspense>
    );
  }
  return <ModaleOggi {...props} />;
}

function ModaleOggi({ evento, prossima, suMontata, onChiudi }: PropsModaleEsito) {
  const { t } = useTranslation('limulo');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const chiudiRef = useRef<HTMLButtonElement | null>(null);
  const positivo = evento.tipo === 'vinto' || evento.tipo === 'torneo-vinto';
  const lingua = useLinguaFormato();
  // L'ora dell'esito (I-328): un esito vecchio non va scambiato per quello della coda che si sta giocando.
  const quando = useMemo(() => quandoEsitoOf(evento.quando, Date.now(), lingua), [evento.quando, lingua]);

  useLivelloIndietro(true, onChiudi);

  // «Gia' vista» e l'aptica quando la modale e' DAVVERO a schermo (🟡1), non quando esce dalla fila. Una
  // volta per esito anche se torna dopo essere stata nascosta: lo tiene `suMontata` (🟢13).
  useEffect(() => {
    suMontata(evento);
  }, [evento, suMontata]);

  useEffect(() => {
    const prima = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    chiudiRef.current?.focus();
    return () => {
      if (prima?.isConnected) prima.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChiudi();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onChiudi]);

  const titolo =
    evento.tipo === 'posto'
      ? t('esito.titolo.posto', { posto: evento.posto })
      : t(`esito.titolo.${evento.tipo}`);
  const Icona = evento.tipo === 'torneo-vinto' ? Trophy : evento.tipo === 'posto' ? Medal : Swords;
  const vai = (dove: string) => {
    haptics.impact('medium');
    onChiudi();
    navigate(dove);
  };
  // r15: una partita di coda o di sit&go non e' un torneo. Il bottone «Vai al torneo» diventa «Torna alla coda» / «Torna ai
  // sit&go» (la scheda della Hub dove si aspetta), e il nome della coda perde il suffisso con cui il giro battezza il torneo.
  const dellaCoda = evento.presentazione === 'coda' || evento.presentazione === 'sit-and-go' ? evento.presentazione : null;
  // Le due pelli, al pixel: primaria (`bg-brand`) o, con la «prossima» in cima, terziaria (`bg-bg-tertiary text-fg`).
  const bottoneAzionePrincipale = (onClick: () => void, testo: string) => (
    <Button
      variant={prossima !== null ? 'neutro' : 'primary'}
      fullWidth
      data-testid="modale-esito-torneo-vai"
      data-torna={dellaCoda ?? undefined}
      onClick={onClick}
      className={cn(
        'rounded-2xl px-0 py-3.5 text-base font-extrabold',
        prossima !== null ? 'text-fg' : 'bg-brand text-brand-fg',
      )}
    >
      {testo}
    </Button>
  );
  const nomeTorneo = evento.torneo && evento.presentazione === 'coda' ? nomeCodaDi(evento.torneo) : evento.torneo;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-4"
      role="alertdialog"
      aria-modal
      data-modale-esito
      aria-labelledby="modale-esito-titolo"
      data-testid="modale-esito"
      data-tipo={evento.tipo}
    >
      <button
        type="button"
        aria-label={t('esito.chiudi')}
        data-testid="modale-esito-velo"
        onClick={onChiudi}
        className="absolute inset-0 bg-black/75"
      />
      <div
        className={cn(
          'relative max-h-full w-full max-w-sm overflow-y-auto rounded-3xl border-2 bg-bg-secondary text-center shadow-[0_20px_60px_rgba(0,0,0,0.6)]',
          positivo ? 'border-success/60' : 'border-border',
        )}
      >
        <div className={cn('px-5 pb-5 pt-5', positivo ? 'bg-success/15' : 'bg-bg-tertiary')}>
          <span
            className={cn(
              'mx-auto grid h-14 w-14 place-items-center rounded-2xl',
              positivo ? 'bg-success/25 text-success' : 'bg-bg text-fg-muted',
            )}
          >
            <Icona aria-hidden className="h-8 w-8" strokeWidth={2.4} />
          </span>
          <p id="modale-esito-titolo" data-testid="modale-esito-titolo" className="mt-3 text-2xl font-extrabold leading-tight text-fg">
            {titolo}
          </p>
          {evento.avversario ? (
            <p data-testid="modale-esito-avversario" className="mt-1 text-sm font-bold text-fg">
              {t('esito.contro', { nome: evento.avversario })}
            </p>
          ) : null}
          {nomeTorneo ? (
            <p data-testid="modale-esito-torneo" className="mt-1 text-sm font-semibold text-fg-muted">
              {nomeTorneo}
            </p>
          ) : null}
          {quando !== null ? (
            <p data-testid="modale-esito-quando" className="mt-1 text-xs font-medium text-fg-muted">
              {t(`esito.quando.${quando.chiave}`, quando.valori)}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 px-5 pb-5 pt-5">
          {prossima !== null ? (
            <button
              type="button"
              data-testid="modale-esito-prossima"
              onClick={() => vai(limuloMatchUrl(evento.tournamentId, prossima))}
              className="w-full rounded-2xl bg-brand py-3.5 text-base font-extrabold text-brand-fg transition-opacity active:opacity-80"
            >
              {t('esito.prossima')}
            </button>
          ) : null}
          {dellaCoda === null ? (
            bottoneAzionePrincipale(() => vai(limuloTournamentUrl(evento.tournamentId)), t('esito.vaiTorneo'))
          ) : (
            <ConTornaAllaCoda>
              {(torna) =>
                bottoneAzionePrincipale(
                  () => {
                    haptics.impact('medium');
                    onChiudi();
                    void torna(dellaCoda, evento.gameId);
                  },
                  dellaCoda === 'coda' ? t('esito.tornaCoda') : t('esito.tornaSitngo'),
                )
              }
            </ConTornaAllaCoda>
          )}
          <button
            type="button"
            ref={chiudiRef}
            data-testid="modale-esito-chiudi"
            onClick={() => {
              haptics.selection();
              onChiudi();
            }}
            className="w-full rounded-2xl py-2.5 text-sm font-bold text-fg-muted transition-opacity active:opacity-70"
          >
            {t('esito.chiudi')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Porta il gesto «Torna alla coda» / «Torna ai sit&go» (r15) a chi disegna il bottone. Un componente a parte perche' il gesto
 * ha il suo hook (store e router), che la modale di un torneo vero non ha bisogno di montare: il bottone resta UNO solo.
 */
function ConTornaAllaCoda({
  children,
}: {
  children: (torna: (presentazione: 'coda' | 'sit-and-go', gameId: string | null) => Promise<void>) => React.ReactNode;
}) {
  return <>{children(useTornaAllaCoda())}</>;
}
